import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { History as HistoryIcon, Inbox } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { StatusBadge } from "@/components/StatusBadge";
import { api, apiErrorMessage, formatTanggalID } from "@/lib/api";

const JENIS_FILTER = [
  { value: "all", label: "Semua Jenis" },
  { value: "Cuti", label: "Cuti" },
  { value: "Izin", label: "Izin" },
  { value: "Sakit", label: "Sakit" },
  { value: "Tugas Luar", label: "Tugas Luar" },
  { value: "Istirahat Haid", label: "Istirahat Haid" },
  { value: "Istirahat Melahirkan / Gugur Kandungan", label: "Istirahat Melahirkan" },
];

const STATUS_FILTER = [
  { value: "all", label: "Semua Status" },
  { value: "Menunggu", label: "Menunggu" },
  { value: "Disetujui", label: "Disetujui" },
  { value: "Ditolak", label: "Ditolak" },
];

/**
 * Modal riwayat pengajuan Cuti/Izin/Sakit dsb per pegawai.
 * Data diambil dari GET /requests?user_id=<uid>.
 */
export function HistoryPengajuanDialog({ pegawai, open, onClose }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [jenis, setJenis] = useState("all");
  const [status, setStatus] = useState("all");

  useEffect(() => {
    if (!open || !pegawai?.user_id) return;
    let cancelled = false;
    setLoading(true);
    setJenis("all");
    setStatus("all");
    api
      .get("/requests", { params: { user_id: pegawai.user_id } })
      .then(({ data }) => {
        if (!cancelled) setRows(Array.isArray(data) ? data : []);
      })
      .catch((e) => {
        if (!cancelled) toast.error(apiErrorMessage(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, pegawai?.user_id]);

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      if (jenis !== "all" && r.jenis !== jenis) return false;
      if (status !== "all" && r.status !== status) return false;
      return true;
    });
  }, [rows, jenis, status]);

  const summary = useMemo(() => {
    return rows.reduce(
      (acc, r) => {
        acc.total += 1;
        if (r.status === "Disetujui") acc.disetujui += 1;
        else if (r.status === "Menunggu") acc.menunggu += 1;
        else if (r.status === "Ditolak") acc.ditolak += 1;
        return acc;
      },
      { total: 0, disetujui: 0, menunggu: 0, ditolak: 0 }
    );
  }, [rows]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-4xl max-h-[90vh] flex flex-col"
        data-testid="history-pengajuan-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <HistoryIcon size={18} className="text-teal-700" />
            Riwayat Pengajuan — {pegawai?.nama || "Pegawai"}
          </DialogTitle>
          <DialogDescription>
            {pegawai?.npp ? (
              <>
                NPP <b className="font-mono-clock">{pegawai.npp}</b>
                {pegawai.unit_nama ? <> • Unit {pegawai.unit_nama}</> : null}
              </>
            ) : (
              "Menampilkan seluruh pengajuan Cuti/Izin/Sakit pegawai."
            )}
          </DialogDescription>
        </DialogHeader>

        {/* Summary chips */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <SummaryPill label="Total" value={summary.total} tone="slate" testId="history-total" />
          <SummaryPill label="Disetujui" value={summary.disetujui} tone="emerald" testId="history-disetujui" />
          <SummaryPill label="Menunggu" value={summary.menunggu} tone="amber" testId="history-menunggu" />
          <SummaryPill label="Ditolak" value={summary.ditolak} tone="rose" testId="history-ditolak" />
        </div>

        {/* Filters */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <Select value={jenis} onValueChange={setJenis}>
            <SelectTrigger data-testid="history-filter-jenis">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {JENIS_FILTER.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="history-filter-status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS_FILTER.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        <div className="flex-1 overflow-y-auto rounded-lg border border-slate-200 bg-white">
          <Table>
            <TableHeader className="sticky top-0 bg-slate-50 z-10">
              <TableRow>
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500">Tanggal</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500">Jenis</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500 text-right">Hari</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500">Alasan</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-10 text-slate-500">
                    Memuat riwayat…
                  </TableCell>
                </TableRow>
              ) : filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-14">
                    <div className="mx-auto w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center text-slate-400">
                      <Inbox size={22} />
                    </div>
                    <div className="mt-3 font-semibold text-slate-700">Belum ada riwayat pengajuan</div>
                    <div className="text-xs text-slate-500 mt-1">
                      Coba ubah filter jenis atau status.
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((r) => (
                  <TableRow
                    key={r.id}
                    className="border-b border-slate-100 hover:bg-slate-50/60"
                    data-testid={`history-row-${r.id}`}
                  >
                    <TableCell className="whitespace-nowrap text-sm text-slate-800">
                      <div className="font-semibold">{formatTanggalID(r.tanggal_mulai)}</div>
                      {r.tanggal_selesai && r.tanggal_selesai !== r.tanggal_mulai ? (
                        <div className="text-[11px] text-slate-500">
                          s/d {formatTanggalID(r.tanggal_selesai)}
                        </div>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={r.jenis} />
                      {r.jenis === "Izin" && r.izin_classification ? (
                        <div className="text-[10px] text-sky-800 mt-1">
                          {r.izin_classification}
                          {r.izin_subtype ? ` • ${r.izin_subtype}` : ""}
                        </div>
                      ) : null}
                    </TableCell>
                    <TableCell className="text-right font-mono-clock font-bold text-slate-900">
                      {r.jumlah_hari}
                    </TableCell>
                    <TableCell className="text-sm text-slate-700 max-w-[280px]">
                      <div className="line-clamp-3 whitespace-pre-wrap break-words">
                        {r.alasan || <span className="text-slate-300">—</span>}
                      </div>
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={r.status} testId={`history-status-${r.id}`} />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SummaryPill({ label, value, tone, testId }) {
  const toneMap = {
    slate: "border-slate-200 bg-slate-50 text-slate-800",
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-800",
    amber: "border-amber-200 bg-amber-50 text-amber-800",
    rose: "border-rose-200 bg-rose-50 text-rose-800",
  };
  return (
    <div className={`rounded-lg border px-3 py-2 ${toneMap[tone]}`} data-testid={testId}>
      <div className="text-[10px] uppercase tracking-[0.2em] font-bold opacity-75">{label}</div>
      <div className="font-mono-clock font-bold text-xl leading-tight">{value}</div>
    </div>
  );
}

export default HistoryPengajuanDialog;
